/**
 * Flying into things — the other half of `RB.EXE`'s `s\combat\collide.c`.
 *
 * `collide.ts` is this port of that module's *first* function, the round-vs-
 * object sweep. This is its *second* (`0x162c:0x0560`), the one `intel\move.c`
 * calls once per object per frame to ask whether anybody has run into anybody.
 * They live in separate files here only because one file holding both would
 * pass 400 lines; upstream they are neighbours, and the docblocks point at each
 * other so a reader can see that.
 *
 * AD-017 (`docs/engineering/collisions-design.md`) carries the recovery with
 * addresses. The four facts that shape this module:
 *
 * 1. **One party must be human-flown.** The original tests `self` and `other`
 *    against the player object pointer at `ds:0x431e` and skips the pair if
 *    neither is it. Two AI aircraft never collide, and an AI never rams a
 *    Zeppelin. This port reads "human-flown" as `ai === null`, which is the
 *    same thing in single player and the right generalisation for the
 *    multiplayer in scope.
 * 2. **MIDAIR COLLISIONS gates all of it, gasbags included.** The flag test at
 *    `0x1B08E` sits above the object-kind switch, so a Zeppelin is exactly as
 *    intangible as another scout when the toggle is off. AIRCRAFT MAY BE
 *    DAMAGED gates it too, and is tested first.
 * 3. **Aeroplanes are one sphere; gasbags are a hull of them.** An aeroplane
 *    uses a single per-type radius — `.DAT` offset 172, which this port already
 *    decoded as `collisionSizeRaw` and had never read. A Zeppelin is 17 spheres
 *    in a line and a balloon is 6, rotated into world space by the object's own
 *    attitude, first one to overlap wins.
 * 4. **Trucks and trains are not collidable.** They have hulls, but those exist
 *    for the gunfire sweep; the collision switch falls through for them.
 *
 * This module is pure geometry and returns *findings*. It applies no damage and
 * touches no state — `encounter-tick.ts` turns a finding into the airframe blow
 * and the gasbag's ignition, so that a collision travels through exactly the
 * same damage machinery a burst of fire does.
 */

import { lengthVec3, rotateVec3, type Vec3 } from "../flight/math3d.ts";
import type { Tunables } from "../flight/tunables.ts";
import { targetRadiusM } from "./ballistics.ts";
import { arrivalAspect, subtractVec3, type CombatBody, type HitAspect } from "./geometry.ts";
import { targetProfileOf, type AirborneTargetKind, type TargetKind } from "./target-profiles.ts";

/**
 * One world unit in `RB.EXE` is 1/8 ft (`re-sessions/calib/NOTES.md` §2, the
 * same constant AD-010 read `AA.DAT`'s ranges with). Recovered exactly, so it
 * is a constant here rather than a tunable — there is nothing to calibrate.
 *
 * Note it is deliberately *not* `tunables.targetRadiusMetresPerUnit` (0.035),
 * which is a port-side fit against the baked models for the gunnery radius.
 * Collision size is a different `.DAT` field read by different code, and the
 * original's own unit is exact.
 */
const WORLD_UNIT_M = 0.0381;

/**
 * An aeroplane's collision radius. `.DAT` offset 172 — 100 for the fighters,
 * 180 for the two-seaters, 300 for the heavies — which `collide.c` reads as
 * `TYPEREC[+0xAC]`. The offsets are the same number because the runtime type
 * record *is* the `.DAT` image with no header ahead of it, which is what makes
 * this a recovered radius rather than an invented one.
 *
 * So two scouts touch at 7.6 m apart, a scout and a Gotha at 15.2 m.
 */
export function airframeCollisionRadiusM(collisionSizeRaw: number): number {
  return Math.max(0, collisionSizeRaw) * WORLD_UNIT_M;
}

/** One sphere of a gasbag's hull, in the object's own body frame. */
export interface HullSphere {
  readonly offset: Vec3;
  readonly radiusM: number;
}

/**
 * The recovered hulls, in the engine's body frame (forward −Z, up +Y).
 *
 * The tables are `ds:0x0DFC` (17 spheres, Zeppelin) and `ds:0x0DCC` (6, balloon),
 * each `{int16 x, y, z, r}` at 1/8 ft per unit. Their axes map to this engine's
 * as `z_engine = −y_orig` (the original's +y runs aft, so its blunt end at
 * y = −3300 is the nose) and `y_engine = z_orig`. Every sphere has `x = 0`, so
 * both hulls are bodies of revolution about the long axis and no lateral term
 * survives the transcription.
 *
 * **Sizes are normalised, not absolute.** Each hull is divided by its own
 * bounding radius here and multiplied back up by the *port's* size for that
 * object in `gasbagHullM`. That is not a convenience: the recovered hulls are
 * about twice the extent of the models this port bakes (§`GASBAG_SCALE_NOTE`),
 * and a collision hull that does not agree with the thing on screen is the one
 * bug this feature must not ship with. Carrying the original's *shape* and the
 * port's *size* keeps the two glued together, and the hull will follow
 * automatically if the models are ever rebaked.
 */
interface RecoveredSphere {
  /** Metres forward of the object's origin (nose positive). */
  readonly forwardM: number;
  /** Metres above the object's origin. */
  readonly upM: number;
  readonly radiusM: number;
}

/** `ds:0x0DFC` — 217 m of airship, blunt at the nose and tapered to a point aft. */
const ZEPPELIN_RECOVERED: readonly RecoveredSphere[] = [
  { forwardM: 125.73, upM: -1.52, radiusM: 9.91 },
  { forwardM: 114.3, upM: -1.52, radiusM: 9.91 },
  { forwardM: 102.87, upM: -1.52, radiusM: 9.91 },
  { forwardM: 91.44, upM: -1.52, radiusM: 11.43 },
  { forwardM: 76.2, upM: -1.52, radiusM: 11.43 },
  { forwardM: 60.96, upM: -1.52, radiusM: 11.43 },
  { forwardM: 45.72, upM: -1.52, radiusM: 11.43 },
  { forwardM: 30.48, upM: -1.52, radiusM: 11.43 },
  { forwardM: 15.24, upM: -1.52, radiusM: 11.43 },
  { forwardM: 0, upM: -1.52, radiusM: 11.43 },
  { forwardM: -15.24, upM: -1.52, radiusM: 11.43 },
  { forwardM: -30.48, upM: -1.52, radiusM: 11.43 },
  { forwardM: -45.72, upM: -1.52, radiusM: 11.43 },
  { forwardM: -60.96, upM: -1.52, radiusM: 11.43 },
  { forwardM: -76.2, upM: -1.52, radiusM: 11.43 },
  { forwardM: -83.82, upM: -1.52, radiusM: 7.62 },
  { forwardM: -91.44, upM: -1.52, radiusM: 3.81 },
];

/** `ds:0x0DCC` — a 25 m Caquot envelope with its basket slung underneath. */
const BALLOON_RECOVERED: readonly RecoveredSphere[] = [
  { forwardM: 13.72, upM: -3.81, radiusM: 5.72 },
  { forwardM: 5.33, upM: -3.81, radiusM: 5.72 },
  { forwardM: -3.05, upM: -3.81, radiusM: 5.72 },
  { forwardM: -11.43, upM: -3.81, radiusM: 5.72 },
  { forwardM: 0, upM: -15.24, radiusM: 9.53 },
  { forwardM: 0, upM: -26.67, radiusM: 3.81 },
];

/**
 * How far the recovered hulls overshoot the baked models, for the record.
 * Zeppelin: hull 231 m across against a baked bounding radius of 56.2 m, so
 * 2.06×. Balloon: 61 m against 13.4 m, so 2.27×. The recovered figures are the
 * historical ones (an L-30 was 198 m, a Caquot 25 m) and `AA.DAT`'s independent
 * use of the same 1/8 ft unit says the transcription is right, so **the baked
 * gasbags look like they are one scale-exponent step too small** — the same
 * class of fault the terrain lane found in `TERRn` (lessons.md, "128× too
 * small"). Reported here rather than fixed: `BALLOON.TBL`/`ZEPPELIN.TBL` belong
 * to the bake pipeline, not to this lane.
 */
export const GASBAG_SCALE_NOTE =
  "recovered gasbag hulls run ~2x the baked model extent; hulls are normalised to the baked size";

function boundingRadiusOf(spheres: readonly RecoveredSphere[]): number {
  return spheres.reduce(
    (widest, sphere) => Math.max(widest, Math.hypot(sphere.forwardM, sphere.upM) + sphere.radiusM),
    0,
  );
}

function normalize(spheres: readonly RecoveredSphere[]): readonly HullSphere[] {
  const extent = boundingRadiusOf(spheres);
  return spheres.map((sphere) => ({
    offset: { x: 0, y: sphere.upM / extent, z: -sphere.forwardM / extent },
    radiusM: sphere.radiusM / extent,
  }));
}

/** The hulls as unit spheres — every offset and radius divided by the hull's own extent. */
const NORMALIZED_HULLS: Readonly<Record<AirborneTargetKind, readonly HullSphere[]>> = {
  zeppelin: normalize(ZEPPELIN_RECOVERED),
  balloon: normalize(BALLOON_RECOVERED),
};

/**
 * The hull for a gasbag kind, scaled to the size this port actually draws it,
 * or `null` for anything the original's switch does not collide with — which is
 * every ground kind, trucks and trains included.
 */
export function gasbagHullM(kind: TargetKind, tunables: Tunables): readonly HullSphere[] | null {
  const normalized = kind === "zeppelin" || kind === "balloon" ? NORMALIZED_HULLS[kind] : null;
  if (!normalized) return null;
  const scaleM = targetRadiusM(targetProfileOf(kind).targetSizeRaw, tunables);
  return normalized.map((sphere) => ({
    offset: { x: sphere.offset.x * scaleM, y: sphere.offset.y * scaleM, z: sphere.offset.z * scaleM },
    radiusM: sphere.radiusM * scaleM,
  }));
}

/** The outer radius a hull needs before any of its spheres can be reached. */
export function hullExtentM(hull: readonly HullSphere[]): number {
  return hull.reduce((widest, sphere) => Math.max(widest, lengthVec3(sphere.offset) + sphere.radiusM), 0);
}

/**
 * Which hull sphere an aeroplane of `radiusM` at `point` is inside, or `null`.
 *
 * The original rotates each sphere's body-frame offset by the object's attitude
 * and measures from there, which is what keeps a banked Zeppelin's hull on its
 * hull. First overlap wins and the search stops — `collide.c` breaks out of the
 * loop on the first hit too, because the sphere's index is the gas cell the blow
 * lands on and a second one would be a second blow.
 */
export function hullSphereHit(
  body: CombatBody,
  hull: readonly HullSphere[],
  point: Vec3,
  radiusM: number,
): number | null {
  const offset = subtractVec3(point, body.position);
  if (lengthVec3(offset) > hullExtentM(hull) + radiusM) return null;
  for (let index = 0; index < hull.length; index += 1) {
    const sphere = hull[index];
    if (!sphere) continue;
    const centre = rotateVec3(body.orientation, sphere.offset);
    const gap = Math.hypot(offset.x - centre.x, offset.y - centre.y, offset.z - centre.z);
    if (gap < sphere.radiusM + radiusM) return index;
  }
  return null;
}

/** An aeroplane as the collision check sees it. */
export interface RamAircraft {
  readonly id: string;
  readonly body: CombatBody;
  readonly radiusM: number;
  /** `ai === null` — the original's `ds:0x431e` test, generalised. */
  readonly human: boolean;
  /** Both realism gates for *this* aeroplane, already resolved. */
  readonly collisionsEnabled: boolean;
  /** A wreck still in the air is solid; one that has hit the ground is gone (AD-017 §5.4). */
  readonly present: boolean;
}

/** A gasbag as the collision check sees it. */
export interface RamGasbag {
  readonly id: string;
  readonly body: CombatBody;
  readonly hull: readonly HullSphere[];
  readonly present: boolean;
}

export interface RamInput {
  readonly aircraft: readonly RamAircraft[];
  readonly gasbags: readonly RamGasbag[];
}

export interface RamCollision {
  readonly aircraftId: string;
  readonly otherId: string;
  /** `true` for aeroplane-into-aeroplane, `false` for aeroplane-into-gasbag. */
  readonly otherIsAircraft: boolean;
  /** Which hull sphere took it — the Zeppelin's gas cell. `null` against an aeroplane. */
  readonly cellIndex: number | null;
  /** The face of `aircraftId` that struck, off the closing direction. */
  readonly aspect: HitAspect;
}

/** The closing direction, for the aspect. Falls back to the line of centres when both are still. */
function closingDirection(one: CombatBody, other: CombatBody): Vec3 {
  const relative = subtractVec3(one.velocity, other.velocity);
  return lengthVec3(relative) > 1e-6 ? relative : subtractVec3(other.position, one.position);
}

function collidable(entry: RamAircraft): boolean {
  return entry.present && entry.collisionsEnabled;
}

/**
 * Every collision on this tick, in roster order so the result is deterministic.
 *
 * A pair is tested once, not twice — the original walks a list per object and
 * would find the same pair from both ends, but it destroys both parties on the
 * first find and flags them, so the second look does nothing. Reporting the pair
 * once and letting the caller wound both is the same behaviour without relying
 * on flag order.
 */
export function detectCollisions(input: RamInput): readonly RamCollision[] {
  const collisions: RamCollision[] = [];
  for (let i = 0; i < input.aircraft.length; i += 1) {
    const one = input.aircraft[i];
    if (!one || !collidable(one)) continue;

    for (let j = i + 1; j < input.aircraft.length; j += 1) {
      const other = input.aircraft[j];
      if (!other || !collidable(other)) continue;
      // The original's `ds:0x431e` test: a pair with no human in it is skipped.
      if (!one.human && !other.human) continue;
      const gap = lengthVec3(subtractVec3(other.body.position, one.body.position));
      if (gap >= one.radiusM + other.radiusM) continue;
      collisions.push({
        aircraftId: one.id,
        otherId: other.id,
        otherIsAircraft: true,
        cellIndex: null,
        aspect: arrivalAspect(one.body, closingDirection(one.body, other.body)),
      });
    }

    // A gasbag has no pilot, so the human test reduces to "is this aeroplane one".
    if (!one.human) continue;
    for (const gasbag of input.gasbags) {
      if (!gasbag.present) continue;
      const cellIndex = hullSphereHit(gasbag.body, gasbag.hull, one.body.position, one.radiusM);
      if (cellIndex === null) continue;
      collisions.push({
        aircraftId: one.id,
        otherId: gasbag.id,
        otherIsAircraft: false,
        cellIndex,
        aspect: arrivalAspect(one.body, closingDirection(one.body, gasbag.body)),
      });
    }
  }
  return collisions;
}
